import styled, { css } from 'styled-components';
import { motion } from 'motion/react';
import { FrostedGlassLight } from '@seed-health/tokens';
import {
  mqMediumMin,
  mqLargeMin,
  mqLargeMax,
} from 'styled/tools/media-queries';
import { FontFamily } from 'styled/fonts/font-family';
import { TypeBody, TypeBodyLarge } from 'components/library/Typography';
import { Grid } from 'styled/layouts/grid';
import Button from 'components/library/Button';

const fakeGrid = (cols: number) => css`
  width: calc(${cols} * var(--grid-col-width) + (${cols} - 1) * 20px);
`;

const glassEffectDark = css`
  background: var(--scrim-glass-dark-35);
  backdrop-filter: ${FrostedGlassLight};
  -webkit-backdrop-filter: ${FrostedGlassLight};
`;

export const MicrobiomeContainer = styled.section``;

export const FlexContainer = styled(TypeBody)`
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
`;

export const BookContainer = styled(FlexContainer)`
  border-radius: var(--radius-full);
  padding: 10px var(--spacing-16);
  ${glassEffectDark}

  && svg * {
    color: var(--fill-primary);
  }
`;

export const MicrobiomeHeader = styled(motion.header)`
  color: var(--fill-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: var(--spacing-16);
  z-index: 10;
`;

export const FullscreenButton = styled.button`
  ${glassEffectDark}
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 10px var(--spacing-16);
  border-radius: var(--radius-full);

  && svg * {
    color: var(--fill-primary);
  }
`;

export const ControlsContainer = styled(motion.div)`
  & > * + * {
    margin-top: var(--spacing-8);
  }
  background-color: var(--scrim-glass-light-20);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border-radius: var(--radius-md);
  position: absolute;
  bottom: var(--spacing-16);
  left: var(--spacing-16);
  right: var(--spacing-16);
  padding: var(--spacing-16);
  z-index: 10;
  @media ${mqLargeMin} {
    width: 416px;
  }
`;

export const Controls = styled(motion.div)`
  align-items: center;
  display: flex;
  gap: var(--spacing-8);
  flex: 1;
  width: 100%;
`;

export const ProgressWrapper = styled(motion.div)`
  border-radius: var(--radius-full);
  height: 36px;
  width: fit-content;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

export const ProgressBuffer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const ProgressBar = styled.div`
  background-color: rgba(0, 0, 0, 0.1);
  height: 100%;
  width: 100%;
  inset: 0;
  transform-origin: left;
  position: absolute;
  pointer-events: none;
  transform: scaleX(0);
  z-index: 3;
`;

export const ProgressButton = styled(motion.button)`
  color: var(--fill-brand);
  font-family: ${FontFamily.sans};
  font-size: var(--font-size-350);
  font-weight: 350;
  cursor: pointer;
  appearance: none;
  background-color: var(--fill-primary);
  border: none;
  display: flex;
  gap: var(--spacing-8);
  align-items: center;
  padding: 10px;
  z-index: 2;
`;

export const BiomeMedia = styled.video`
  border-radius: var(--radius-xl);
  height: 100%;
  object-fit: cover;
  width: 100%;

  @media ${mqLargeMax} {
    border-radius: 0;
  }
`;

export const ChapterButton = styled.button`
  background-color: var(--fill-brand);
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  height: 12px;
  width: 12px;
`;

export const ChapterConnector = styled.div`
  background-color: var(--fill-brand);
  opacity: 0.2;
  height: 2px;
  align-self: center;
  flex: 1;
  min-width: 8px;
  max-width: 40px;

  @media ${mqLargeMax} {
    min-width: 4px;
    max-width: 20px;
  }

  @media (max-width: 400px) {
    min-width: 2px;
    max-width: 12px;
  }
`;

export const CarouselContainer = styled.div`
  aspect-ratio: 1440 / 760;
  display: grid;
  margin-inline: auto;
  max-width: 1440px;
  overflow: hidden;
  position: relative;
  width: 100%;

  .swiper {
    border-radius: var(--radius-xl);
    max-width: 100%;
    width: 100%;
  }

  .swiper-wrapper {
    width: 100%;
  }

  @media ${mqLargeMax} {
    aspect-ratio: 9/16;
    .swiper {
      border-radius: 0;
    }
  }
`;

export const MicrobioSection = styled.section`
  background: rgba(252, 252, 247, 1);
  padding-inline: var(--spacing-16);

  @media ${mqMediumMin} {
    padding-inline: var(--spacing-24);
  }

  @media ${mqLargeMin} {
    padding-inline: var(--spacing-32);
  }

  @media ${mqLargeMax} {
    padding-block: 46px;
  }
`;

export const MicrobiomeIntro = styled(Grid)`
  place-items: center;
  margin-inline: auto;
  max-width: 1440px;
  width: 100%;

  @media ${mqLargeMin} {
    position: relative;
    min-height: 760px;
  }
`;

export const MicrobiomeIntroBook = styled.div`
  grid-column: 1 / span 2;
  justify-self: start;
  color: var(--fill-brand);
  font-variant-numeric: slashed-zero;
  font-family: var(--ff-sans, 'Seed Sans');
  font-size: 20px;
  font-style: normal;
  font-weight: 350;
  line-height: 90%; /* 18px */
  letter-spacing: -0.6px;

  svg {
    vertical-align: text-top;
  }
`;

export const MicrobiomeIntroContent = styled.div`
  & > * + * {
    margin-top: var(--spacing-32);
  }
  grid-column: 1 / span 5;

  @media ${mqLargeMax} {
    grid-column: 1 / -1;
  }
`;

export const MicrobiomeIntroImage = styled(motion.figure)`
  --padding: calc(100% - 1440px);
  background-color: rgba(238, 238, 233, 1);
  aspect-ratio: 1 / 1;
  border-radius: var(--radius-xl);
  overflow: hidden;

  @media ${mqLargeMax} {
    grid-column: 1 / -1;
    order: 2;
    width: 100% !important;
    position: relative;
  }

  @media ${mqLargeMin} {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate3d(0, -50%, 0);
    transform-origin: left;
    ${fakeGrid(5)};
  }

  & > picture,
  & img,
  & > video {
    block-size: 100%;
    inline-size: 100%;
    object-fit: cover;
  }
`;

export const MicrobiomeIntroStill = styled.img`
  display: none;

  @media (prefers-reduced-motion: reduce) {
    display: block;
  }
`;

export const MicrobiomeFooter = styled.div`
  grid-column: 1 / span 3;
  align-self: end;
  justify-self: start;
  padding-block: 46px;
  color: var(--fill-brand);

  @media ${mqLargeMax} {
    display: none; // Hide on mobile
  }
`;

export const MicrobiomeFooterLabel = styled.span`
  letter-spacing: 0.24px;
  font-family: var(--ff-sans-mono, ${FontFamily.mono});
  text-transform: uppercase;
`;

export const MicrobiomeFooterTitle = styled.span`
  font-weight: 500;
  letter-spacing: -0.12px;
  margin-left: var(--spacing-8);
`;

export const Panel = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);
  max-width: 416px;
`;

export const PanelTitle = styled(TypeBodyLarge)`
  color: var(--fill-brand);
  font-weight: 500;
`;

export const PlayCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #36542d; /* TODO: move to colors file */
  color: var(--fill-primary);
`;

export const DiscoverButton = styled(Button)`
  padding-top: 5px;
  padding-bottom: 5px;
  padding-right: 5px;
  padding-left: 34px;
  gap: 18px;
`;

export const DiscoverButtonSmall = styled(Button)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding-top: 5px;
  padding-bottom: 5px;
  padding-right: 5px;
  padding-left: 34px;
  gap: 18px;
`;
